'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Edit2,
  Calendar as CalendarIcon,
  Clock,
  User,
  Phone,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  MapPin,
  Loader2,
  Check
} from 'lucide-react';
import { Service, Staff, DateItem, TimeSlot } from './types';

interface BookingSlideOverDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
  business: any;
  currentBranch: any;
  staffList: Staff[];
  preSelectedStaffId?: string;
  onOpenAppointmentDetails?: (booking: any) => void;
  initialBooking?: any; // Para modo edición / reagendar
  onRescheduleSuccess?: (updatedBooking: any) => void;
}

export const BookingSlideOverDrawer: React.FC<BookingSlideOverDrawerProps> = ({
  isOpen,
  onClose,
  service,
  business,
  currentBranch,
  staffList,
  preSelectedStaffId,
  onOpenAppointmentDetails,
  initialBooking,
  onRescheduleSuccess
}) => {
  // Pasos: 1 = Staff, 2 = Fecha y Hora, 3 = Detalles / Asignación, 4 = Éxito
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Estados de Selección
  const [selectedStaffId, setSelectedStaffId] = useState<string>('any');
  const [selectedStaffName, setSelectedStaffName] = useState<string>('No tengo preferencia');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');

  // Fechas disponibles
  const [availableDates, setAvailableDates] = useState<DateItem[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);

  // Datos del Cliente (Recuperados de localStorage del negocio específico si existen)
  const [clientPhone, setClientPhone] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [clientLastName, setClientLastName] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');
  const [isForAnotherPerson, setIsForAnotherPerson] = useState<boolean>(false);
  const [anotherPersonName, setAnotherPersonName] = useState<string>('');
  const [anotherPersonPhone, setAnotherPersonPhone] = useState<string>('');
  const [isEditingClientData, setIsEditingClientData] = useState<boolean>(false);

  // Estados de envío y resultado
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmedBooking, setConfirmedBooking] = useState<any>(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState<boolean>(false);
  const [whatsAppSent, setWhatsAppSent] = useState<boolean>(false);
  const [whatsAppFeedback, setWhatsAppFeedback] = useState<string | null>(null);

  const datesCarouselRef = useRef<HTMLDivElement>(null);

  // Cargar perfil guardado de cliente para este negocio específico
  useEffect(() => {
    try {
      const slug = business?.slug || 'default';
      const storedProfile = localStorage.getItem(`calenddy_client_profile_${slug}`);
      if (storedProfile) {
        const p = JSON.parse(storedProfile);
        if (p.phone) setClientPhone(p.phone);
        if (p.firstName || p.name) setClientName(p.firstName || p.name);
        if (p.lastName) setClientLastName(p.lastName);
        if (p.email) setClientEmail(p.email);
      }
    } catch (e) {
      console.error(e);
    }
  }, [business?.slug]);

  // Profesionales que realmente brindan este servicio específico
  const eligibleStaffList = useMemo(() => {
    if (!service || !Array.isArray(staffList)) return staffList || [];
    if (Array.isArray(service.staff) && service.staff.length > 0) {
      const allowedIds = new Set(service.staff.map((s) => s.id));
      const filtered = staffList.filter((st) => allowedIds.has(st.id));
      return filtered.length > 0 ? filtered : staffList;
    }
    return staffList;
  }, [service, staffList]);

  // Profesional seleccionado actualmente
  const currentStaffObj = useMemo(() => {
    return eligibleStaffList.find((s) => s.id === selectedStaffId) || staffList.find((s) => s.id === selectedStaffId);
  }, [eligibleStaffList, staffList, selectedStaffId]);

  const selectedStaffAvatar = selectedStaffId !== 'any' ? currentStaffObj?.avatarUrl : null;

  // Generar los próximos días hábiles según la disponibilidad real del empleado seleccionado
  useEffect(() => {
    if (!isOpen) return;

    const daysMap = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const monthsMap = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const isDayWorking = (dayOfWeek: number) => {
      // 1. Si hay un profesional específico seleccionado:
      if (selectedStaffId && selectedStaffId !== 'any') {
        if (currentStaffObj && Array.isArray(currentStaffObj.schedules) && currentStaffObj.schedules.length > 0) {
          const sc = currentStaffObj.schedules.find((item: any) => item.dayOfWeek === dayOfWeek);
          return sc ? Boolean(sc.enabled) : false;
        }
      } else {
        // 2. Si eligió "No tengo preferencia":
        const candidates = eligibleStaffList.length > 0 ? eligibleStaffList : staffList;
        if (candidates.length > 0) {
          const anyWorks = candidates.some((st) => {
            if (Array.isArray(st.schedules) && st.schedules.length > 0) {
              const sc = st.schedules.find((item: any) => item.dayOfWeek === dayOfWeek);
              return sc ? Boolean(sc.enabled) : false;
            }
            return dayOfWeek !== 0; // Por defecto lunes a sábado
          });
          if (anyWorks) return true;
        }
      }

      // 3. Fallback a horarios generales del negocio
      if (Array.isArray(business?.schedules) && business.schedules.length > 0) {
        const bizSc = business.schedules.find((item: any) => item.dayOfWeek === dayOfWeek && item.staffId === null);
        if (bizSc) return Boolean(bizSc.enabled);
      }

      // Por defecto domingo (0) cerrado, lunes a sábado (1..6) abierto
      return dayOfWeek !== 0;
    };

    const newDates: DateItem[] = [];
    const today = new Date();
    const maxDays = business?.maxAdvanceDays || 45;

    for (let i = 0; i < maxDays && newDates.length < 30; i++) {
      const d = new Date();
      d.setDate(today.getDate() + i);
      const dayOfWeek = d.getDay();

      if (isDayWorking(dayOfWeek)) {
        const dayName = daysMap[dayOfWeek];
        const dayNum = d.getDate().toString();
        const monthShort = monthsMap[d.getMonth()];
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const fullDate = `${year}-${month}-${day}`;
        newDates.push({ dayName, dayNum, monthShort, fullDate, isWorkingDay: true });
      }
    }

    setAvailableDates(newDates);
  }, [isOpen, selectedStaffId, staffList, eligibleStaffList, currentStaffObj, business]);

  // Inicializar modo edición / reagendar o preselección desde catálogo
  useEffect(() => {
    if (!isOpen) return;

    if (initialBooking) {
      setSelectedStaffId(initialBooking.staffId || 'any');
      setSelectedStaffName(initialBooking.staffName || 'No tengo preferencia');
      if (initialBooking.dateStr || initialBooking.date) {
        setSelectedDate(initialBooking.dateStr || initialBooking.date);
      }
      if (initialBooking.startTime || initialBooking.time) {
        setSelectedTime(initialBooking.startTime || initialBooking.time);
      }
      setCurrentStep(2); // Comienza en fecha/hora para reagendar
    } else {
      // Si el cliente ya había seleccionado un profesional en el catálogo previamente:
      if (preSelectedStaffId && preSelectedStaffId !== 'any') {
        const found = staffList.find((s) => s.id === preSelectedStaffId);
        // Validar si ese profesional efectivamente brinda este servicio
        const doesService = !service?.staff?.length || service.staff.some((s) => s.id === preSelectedStaffId);

        if (found && doesService) {
          setSelectedStaffId(found.id);
          setSelectedStaffName(found.name);
          setSelectedDate(''); // No preseleccionar fecha inicialmente
          setSelectedTime('');
          setSlots([]);
          setCurrentStep(2); // ¡Avanza directo al paso 2 de Fecha y Hora sin volver a pedir profesional!
        } else {
          setSelectedStaffId('any');
          setSelectedStaffName('No tengo preferencia');
          setSelectedDate('');
          setSelectedTime('');
          setSlots([]);
          setCurrentStep(1);
        }
      } else {
        setSelectedStaffId('any');
        setSelectedStaffName('No tengo preferencia');
        setSelectedDate('');
        setSelectedTime('');
        setSlots([]);
        setCurrentStep(1);
      }

      setConfirmedBooking(null);
      setErrorMessage('');
    }
  }, [initialBooking, isOpen, preSelectedStaffId, service, staffList]);

  // Cargar horarios disponibles en vivo desde PostgreSQL
  useEffect(() => {
    if (!isOpen || !selectedDate || !service) return;
    const srvId = service.id;
    const srvDuration = service.duration;

    let isMounted = true;
    async function fetchAvailability() {
      setLoadingSlots(true);
      try {
        const slug = business?.slug || 'juanrojas';
        const targetBranchSlug = currentBranch?.slug || '';
        const branchParam = targetBranchSlug ? `&branchSlug=${encodeURIComponent(targetBranchSlug)}` : '';
        const res = await fetch(
          `/api/availability?slug=${encodeURIComponent(slug)}&date=${selectedDate}&serviceIds=${srvId}&duration=${srvDuration}&staffId=${selectedStaffId}${branchParam}`
        );
        const data = await res.json();
        if (isMounted) {
          if (data.success && Array.isArray(data.slots)) {
            setSlots(data.slots);
          } else {
            setSlots([]);
          }
        }
      } catch (e) {
        console.error('Error fetching availability slots:', e);
        if (isMounted) setSlots([]);
      } finally {
        if (isMounted) setLoadingSlots(false);
      }
    }

    fetchAvailability();
    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedDate, selectedStaffId, service, business?.slug, currentBranch?.slug]);

  if (!isOpen || !service) return null;

  // Formato de precio y fecha
  const priceVal = service.cashPrice || service.price || 49000;
  const priceFormatted = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(priceVal);

  // Formateo de fecha larga para confirmación
  let dateFormattedLong = selectedDate;
  try {
    const parts = selectedDate.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const d = new Date(year, month, day, 12, 0, 0);
      const dayName = d.toLocaleDateString('es-AR', { weekday: 'long' });
      const monthName = d.toLocaleDateString('es-AR', { month: 'long' });
      dateFormattedLong = `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}, ${day} de ${monthName} de ${year}`;
    }
  } catch (e) {
    console.error(e);
  }

  // Scroll del carrusel de fechas
  const scrollDates = (direction: 'left' | 'right') => {
    if (datesCarouselRef.current) {
      const offset = direction === 'left' ? -200 : 200;
      datesCarouselRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };

  // Manejar selección de staff
  const handleSelectStaff = (st: Staff) => {
    setSelectedStaffId(st.id);
    setSelectedStaffName(st.name);
    setSelectedDate('');
    setSelectedTime('');
    setSlots([]);
    setCurrentStep(2);
  };

  const handleSelectAnyStaff = () => {
    setSelectedStaffId('any');
    setSelectedStaffName('No tengo preferencia');
    setSelectedDate('');
    setSelectedTime('');
    setSlots([]);
    setCurrentStep(2);
  };

  // Manejar selección de horario
  const handleSelectTimeSlot = (slot: TimeSlot) => {
    setSelectedTime(slot.time);
    if (slot.staffName && selectedStaffId === 'any') {
      // Registrar nombre dinámico de profesional asignado automáticamente
      setSelectedStaffName(slot.staffName);
    }
    setCurrentStep(3);
  };

  // Guardar reserva o reagendar
  const handleFinalizeBooking = async (bookAnother: boolean = false) => {
    setSubmitting(true);
    setErrorMessage('');

    // Validar teléfono y nombre
    const finalPhone = clientPhone.trim();
    const finalFullName = `${clientName.trim()} ${clientLastName.trim()}`.trim();
    if (!finalPhone || !finalFullName) {
      setErrorMessage('Por favor completá tu número de teléfono y nombre completo.');
      setSubmitting(false);
      return;
    }

    // Persistir datos del cliente en localStorage para recordar en este negocio
    const slug = business?.slug || 'default';
    try {
      localStorage.setItem(
        `calenddy_client_profile_${slug}`,
        JSON.stringify({
          phone: finalPhone,
          firstName: clientName.trim(),
          lastName: clientLastName.trim(),
          email: clientEmail.trim()
        })
      );
    } catch (e) {
      console.error(e);
    }

    try {
      // MODO 1: REAGENDAR (PATCH)
      if (initialBooking) {
        const token = initialBooking.accessToken || initialBooking.securityCode || '';
        const res = await fetch(`/api/bookings/${initialBooking.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dateStr: selectedDate,
            startTime: selectedTime,
            staffId: selectedStaffId !== 'any' ? selectedStaffId : undefined,
            accessToken: token,
            securityCode: token
          })
        });

        const data = await res.json();
        if (data.success && data.appointment) {
          // Actualizar en localStorage del negocio correspondiente
          try {
            const storageKey = `calenddy_bookings_${slug}`;
            const stored = localStorage.getItem(storageKey);
            if (stored) {
              const list = JSON.parse(stored);
              const updatedList = list.map((item: any) =>
                item.id === initialBooking.id
                  ? {
                      ...item,
                      date: selectedDate,
                      dateStr: selectedDate,
                      time: selectedTime,
                      startTime: selectedTime,
                      staffName: selectedStaffName
                    }
                  : item
              );
              localStorage.setItem(storageKey, JSON.stringify(updatedList));
            }
          } catch (e) {
            console.error(e);
          }

          if (onRescheduleSuccess) onRescheduleSuccess(data.appointment);
          setConfirmedBooking(data.appointment);
          if (bookAnother) {
            onClose();
          } else {
            setCurrentStep(4);
          }
        } else {
          setErrorMessage(data.error || 'No se pudo reagendar el turno.');
        }
      } else {
        // MODO 2: NUEVA RESERVA (POST)
        const payload = {
          businessSlug: business?.slug || 'juanrojas',
          branchId: currentBranch?.id,
          branchSlug: currentBranch?.slug,
          serviceId: service.id,
          staffId: selectedStaffId,
          dateStr: selectedDate,
          startTime: selectedTime,
          clientName: isForAnotherPerson && anotherPersonName ? anotherPersonName : finalFullName,
          clientPhone: isForAnotherPerson && anotherPersonPhone ? anotherPersonPhone : finalPhone,
          clientEmail: clientEmail.trim() || undefined,
          subProfileName: isForAnotherPerson ? finalFullName : undefined,
          notes: isForAnotherPerson ? `Agendado por titular: ${finalFullName} (${finalPhone})` : ''
        };

        const res = await fetch('/api/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await res.json();
        if (data.success && data.appointment) {
          // Guardar en localStorage de reservas del dispositivo para este negocio
          const newBookingItem = {
            id: data.appointment.id,
            businessSlug: slug,
            branchName: currentBranch?.name || business?.name || 'Sede Central',
            serviceName: service.name,
            staffName: selectedStaffName,
            date: selectedDate,
            dateStr: selectedDate,
            time: selectedTime,
            startTime: selectedTime,
            duration: service.duration,
            price: priceVal,
            clientName: finalFullName,
            clientPhone: finalPhone,
            status: 'CONFIRMED',
            securityCode: data.appointment.securityCode || '804061',
            accessToken: data.appointment.accessToken || '804061',
            createdAt: new Date().toISOString()
          };

          try {
            const storageKey = `calenddy_bookings_${slug}`;
            const stored = localStorage.getItem(storageKey);
            let list = stored ? JSON.parse(stored) : [];
            if (!Array.isArray(list)) list = [];
            list.unshift(newBookingItem);
            localStorage.setItem(storageKey, JSON.stringify(list));
          } catch (e) {
            console.error(e);
          }

          setConfirmedBooking(newBookingItem);
          if (bookAnother) {
            onClose();
          } else {
            setCurrentStep(4);
          }
        } else {
          setErrorMessage(data.error || 'Ocurrió un error al agendar la cita.');
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error de comunicación al agendar.');
    } finally {
      setSubmitting(false);
    }
  };

  // Enviar mensaje de WhatsApp automático directo por Evolution API (estilo Cerini / Appgenda)
  const handleSendWhatsAppConfirmation = async () => {
    const bookingId = confirmedBooking?.id;
    if (!bookingId) return;

    setSendingWhatsApp(true);
    setWhatsAppFeedback(null);

    try {
      const res = await fetch(`/api/bookings/${bookingId}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setWhatsAppSent(true);
        setWhatsAppFeedback(`✓ Confirmación enviada con éxito a tu WhatsApp (${data.recipientPhone || clientPhone})`);
      } else {
        setWhatsAppFeedback(data.error || 'No se pudo enviar el mensaje por WhatsApp en este momento');
      }
    } catch (err: any) {
      setWhatsAppFeedback('Error al comunicarse con el servidor de WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Fondo semitransparente nítido sin borrosidad, con brillo atenuado (Fiel a Cerini / Calendico) */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/35 transition-opacity animate-in fade-in duration-200"
      />

      {/* Contenedor flotante lateral derecho: margen uniforme de 12px a la derecha igualando arriba y abajo, extendido hacia la derecha manteniendo el lateral izquierdo */}
      <div className="fixed inset-0 z-50 flex items-center justify-end p-3 pointer-events-none">
        <div className="pointer-events-auto w-full sm:w-[445px] sm:max-w-[445px] bg-white shadow-2xl rounded-[16px] flex flex-col overflow-hidden animate-in slide-in-from-right duration-300 h-[calc(100dvh-24px)] justify-between relative">
          
          {/* HEADER DEL DRAWER: En paso 4 solo muestra la X a la derecha sin borde inferior (Idéntico a Imagen 5) */}
          {currentStep === 4 ? (
            <div className="px-5 pt-5 pb-0 flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-800 transition cursor-pointer"
                title="Cerrar"
              >
                <X className="w-6 h-6 stroke-[1.6]" />
              </button>
            </div>
          ) : (
            <div className="p-6 border-b border-gray-100 flex items-start justify-between">
              <div className="flex items-center gap-3">
                {currentStep === 3 && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition cursor-pointer"
                    title="Volver"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="min-w-0 pr-2">
                  <h2
                    style={{
                      fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                      fontSize: '30px',
                      fontWeight: 500,
                      lineHeight: '36px',
                      color: '#111827'
                    }}
                    className="tracking-normal"
                  >
                    {currentStep === 1 && 'Elige un profesional'}
                    {currentStep === 2 && 'Elegir horario'}
                    {currentStep === 3 && 'Detalles de la cita'}
                  </h2>
                  <p
                    style={{
                      fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '28px',
                      color: '#111827'
                    }}
                    className="mt-0.5"
                  >
                    {service.name} · Aprox. {service.duration} minutos · {priceFormatted}
                  </p>

                  {/* Badge compacto del profesional seleccionado (Fiel a Calendico Cerini) */}
                  {currentStep === 2 && (
                    <div className="mt-3">
                      <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white shadow-xs">
                        {selectedStaffAvatar ? (
                          <img
                            src={selectedStaffAvatar}
                            alt={selectedStaffName}
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                            <Sparkles className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <span
                          style={{
                            fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                            fontSize: '18px',
                            fontWeight: 400,
                            lineHeight: '28px',
                            color: '#111827'
                          }}
                        >
                          {selectedStaffName}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDate('');
                            setSlots([]);
                            setCurrentStep(1);
                          }}
                          className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0284c7] hover:text-sky-700 ml-1 cursor-pointer transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          <span>Editar</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 transition cursor-pointer shrink-0 ml-2"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* CUERPO PRINCIPAL DEL DRAWER */}
          <div className={`flex-1 ${currentStep === 4 ? 'flex flex-col justify-center px-6 py-4 overflow-hidden -mt-12' : 'overflow-y-auto p-6'}`}>

            {/* PASO 1: SELECCIÓN DE PROFESIONAL (Grilla circular 3 columnas + Sin Preferencia) */}
            {currentStep === 1 && (
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="grid grid-cols-3 gap-y-7 gap-x-3 py-2">
                    {eligibleStaffList.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => handleSelectStaff(st)}
                        className="flex flex-col items-center group cursor-pointer transition-transform active:scale-95 bg-transparent border-0 p-0 focus:outline-none"
                      >
                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-100 group-hover:border-gray-200 shadow-xs relative transition-all duration-200 group-hover:scale-105 group-hover:shadow-md">
                          {st.avatarUrl ? (
                            <img
                              src={st.avatarUrl}
                              alt={st.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div
                              style={{ backgroundColor: st.colorHex || '#10b981' }}
                              className="w-full h-full flex items-center justify-center text-white font-bold text-xl uppercase"
                            >
                              {st.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '28px',
                            color: '#111827'
                          }}
                          className="text-center mt-2 group-hover:text-black transition-colors line-clamp-1"
                        >
                          {st.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Botón Destacado Inferior "No tengo preferencia" */}
                <div className="pt-6 border-t border-gray-100 mt-6">
                  <p
                    style={{
                      fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: '#111827'
                    }}
                    className="text-center mb-2"
                  >
                    máxima disponibilidad
                  </p>
                  <button
                    type="button"
                    onClick={handleSelectAnyStaff}
                    className="w-full py-3.5 px-6 rounded-2xl bg-[#00966b] hover:bg-[#00825c] active:scale-[0.99] flex items-center justify-center gap-2 shadow-sm transition cursor-pointer"
                  >
                    <Sparkles className="w-5 h-5 text-[#d1fae5]" />
                    <span
                      style={{
                        fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                        fontSize: '18px',
                        fontWeight: 400,
                        lineHeight: '28px',
                        color: '#d1fae5'
                      }}
                    >
                      No tengo preferencia
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* PASO 2: SELECTOR DE FECHA Y HORARIO */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Carrusel Horizontal de Fechas */}
                <div className="relative">
                  <div
                    ref={datesCarouselRef}
                    className="flex items-center gap-2.5 overflow-x-auto py-1 scrollbar-none scroll-smooth pr-10"
                  >
                    {availableDates.map((item) => {
                      const isSelected = selectedDate === item.fullDate;
                      return (
                        <button
                          key={item.fullDate}
                          type="button"
                          onClick={() => {
                            setSelectedDate(item.fullDate);
                            setSelectedTime('');
                          }}
                          className={`min-w-[90px] sm:min-w-[94px] py-3.5 px-2.5 rounded-2xl border text-center cursor-pointer transition-all shrink-0 ${
                            isSelected
                              ? 'border-2 border-[#00966b] bg-emerald-50/15 shadow-xs'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              color: '#047857'
                            }}
                            className="block mb-0.5"
                          >
                            {item.dayNum}
                          </span>
                          <span
                            style={{
                              fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              color: '#111827'
                            }}
                            className="block capitalize leading-tight"
                          >
                            {item.dayName}
                          </span>
                          <span
                            style={{
                              fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '20px',
                              color: '#111827'
                            }}
                            className="block capitalize leading-tight mt-0.5"
                          >
                            {item.monthShort}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Flecha derecha para avanzar carrusel */}
                  <button
                    type="button"
                    onClick={() => scrollDates('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-gray-200 bg-white/95 shadow-sm flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer z-10"
                    title="Siguiente"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Lista de Horarios Disponibles: 3 horarios por fila, solo la hora en tarjeta acotada */}
                {selectedDate ? (
                  <div className="space-y-3 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block">
                      Horarios Disponibles
                    </span>

                    {loadingSlots ? (
                      <div className="grid grid-cols-3 gap-2.5 sm:gap-3 py-1">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="h-11 bg-gray-100 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : slots.length === 0 ? (
                      <div className="p-8 text-center bg-gray-50 rounded-2xl border border-gray-100">
                        <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs font-semibold text-gray-700">No hay turnos disponibles para esta fecha</p>
                        <p className="text-[11px] text-gray-400 mt-1">Probá seleccionando otro día en el carrusel de arriba.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2.5 sm:gap-3 max-h-[42vh] overflow-y-auto pr-1 py-1">
                        {slots.map((slot, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleSelectTimeSlot(slot)}
                            style={{
                              fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                              fontSize: '15px',
                              fontWeight: 500,
                              color: '#111827'
                            }}
                            className="py-3 px-2 rounded-xl border border-gray-200 bg-white shadow-xs hover:border-gray-400 hover:bg-gray-50 active:scale-95 text-center flex items-center justify-center transition-all cursor-pointer group"
                          >
                            <span>{slot.time}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* PASO 3: DETALLES DE LA CITA Y ASIGNACIÓN (Yo vs Otra persona) */}
            {currentStep === 3 && (
              <div className="space-y-5">
                {/* Fichas Resumen Editables */}
                <div className="space-y-2.5">
                  {/* Especialista (Limpio sin etiqueta redundante) */}
                  <div className="flex items-center justify-between p-3.5 rounded-2xl border border-gray-200 bg-white shadow-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      {selectedStaffAvatar ? (
                        <img
                          src={selectedStaffAvatar}
                          alt={selectedStaffName}
                          className="w-9 h-9 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                          <Sparkles className="w-4 h-4" />
                        </div>
                      )}
                      <span
                        style={{
                          fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                          fontSize: '18px',
                          fontWeight: 400,
                          lineHeight: '28px',
                          color: '#111827'
                        }}
                        className="truncate"
                      >
                        {selectedStaffName}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0284c7] hover:text-sky-700 ml-2 shrink-0 cursor-pointer transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Editar</span>
                    </button>
                  </div>

                  {/* Fecha y Hora (Limpio sin etiqueta redundante y sin icono) */}
                  <div className="flex items-center justify-between p-3.5 rounded-2xl border border-gray-200 bg-white shadow-xs">
                    <span
                      style={{
                        fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '26px',
                        color: '#111827'
                      }}
                      className="min-w-0 pr-2"
                    >
                      {dateFormattedLong}, {selectedTime}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0284c7] hover:text-sky-700 shrink-0 cursor-pointer transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>

                {/* Sección "Reservado para" */}
                <div>
                  <span
                    style={{
                      fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#111827'
                    }}
                    className="block mb-2.5"
                  >
                    Reservado para
                  </span>

                  <div className="space-y-2.5">
                    {/* Opción 1: Titular */}
                    <div
                      onClick={() => setIsForAnotherPerson(false)}
                      className={`p-3.5 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                        !isForAnotherPerson
                          ? 'border-2 border-[#00966b] bg-emerald-50/15 shadow-xs'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          !isForAnotherPerson ? 'border-[#00966b] bg-[#00966b]' : 'border-gray-300'
                        }`}>
                          {!isForAnotherPerson && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            color: '#111827'
                          }}
                        >
                          {clientName} {clientLastName}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditingClientData(!isEditingClientData);
                        }}
                        className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0284c7] hover:text-sky-700 cursor-pointer transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        <span>{isEditingClientData ? 'Listo' : 'Editar'}</span>
                      </button>
                    </div>

                    {/* Formulario desplegable si quiere editar sus datos */}
                    {isEditingClientData && !isForAnotherPerson && (
                      <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 space-y-2.5">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-gray-600 uppercase">Nombre</label>
                            <input
                              type="text"
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                              className="w-full text-xs font-semibold p-2 bg-white rounded-lg border border-gray-200 focus:outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-600 uppercase">Apellido</label>
                            <input
                              type="text"
                              value={clientLastName}
                              onChange={(e) => setClientLastName(e.target.value)}
                              className="w-full text-xs font-semibold p-2 bg-white rounded-lg border border-gray-200 focus:outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-600 uppercase">Teléfono de WhatsApp</label>
                          <input
                            type="tel"
                            value={clientPhone}
                            onChange={(e) => setClientPhone(e.target.value)}
                            className="w-full text-xs font-semibold p-2 bg-white rounded-lg border border-gray-200 focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Opción 2: Para otra persona */}
                    <div
                      onClick={() => setIsForAnotherPerson(true)}
                      className={`p-3.5 rounded-2xl border cursor-pointer transition-all ${
                        isForAnotherPerson
                          ? 'border-2 border-[#00966b] bg-emerald-50/15 shadow-xs'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isForAnotherPerson ? 'border-[#00966b] bg-[#00966b]' : 'border-gray-300'
                        }`}>
                          {isForAnotherPerson && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            color: '#111827'
                          }}
                        >
                          Para otra persona
                        </span>
                      </div>

                      {/* Campos adicionales si es para otra persona */}
                      {isForAnotherPerson && (
                        <div className="mt-3.5 pt-3 border-t border-emerald-100 space-y-2.5">
                          <div>
                            <label className="text-[10px] font-bold text-gray-600 uppercase">Nombre de la persona</label>
                            <input
                              type="text"
                              placeholder="Ej: Sofía Rojas"
                              value={anotherPersonName}
                              onChange={(e) => setAnotherPersonName(e.target.value)}
                              className="w-full text-xs font-semibold p-2.5 bg-white rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-600 uppercase">Teléfono de la persona</label>
                            <input
                              type="tel"
                              placeholder="Ej: 11 5566-7788"
                              value={anotherPersonPhone}
                              onChange={(e) => setAnotherPersonPhone(e.target.value)}
                              className="w-full text-xs font-semibold p-2.5 bg-white rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {errorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-medium text-red-600">
                    {errorMessage}
                  </div>
                )}

                {/* Acciones de Cierre */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => handleFinalizeBooking(true)}
                    style={{
                      fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#00966b'
                    }}
                    className="block text-center w-full mb-3 cursor-pointer hover:underline transition"
                  >
                    Finalizar y agendar otra cita
                  </button>

                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => handleFinalizeBooking(false)}
                    style={{
                      fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                    className="w-full py-4 px-6 rounded-2xl bg-[#00966b] hover:bg-[#00825c] active:scale-[0.99] text-white flex items-center justify-center gap-2 shadow-sm transition cursor-pointer disabled:opacity-50"
                  >
                    <span>{submitting ? 'Confirmando cita...' : 'Finalizar'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* PASO 4: ÉXITO ("¡Cita agendada!" - Réplica exacta Imagen 5) */}
            {currentStep === 4 && (
              <div className="flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-200">
                {/* Círculo con Tilde Verde idéntico a Imagen 5 (fondo blanco puro + borde verde + tilde verde) */}
                <div className="w-[82px] h-[82px] rounded-full border-[2.5px] border-[#00966b] bg-white flex items-center justify-center mx-auto mb-5 text-[#00966b] shadow-xs">
                  <Check className="w-10 h-10 stroke-[2.5]" />
                </div>

                <h2
                  style={{
                    fontFamily: 'var(--font-plus-jakarta), "Plus Jakarta Display", "Inter var", sans-serif',
                    fontSize: '30px',
                    lineHeight: '36px',
                    letterSpacing: '0',
                    fontWeight: 500,
                    color: '#111827'
                  }}
                  className="tracking-normal text-center"
                >
                  ¡Cita agendada!
                </h2>
                <p className="text-[15px] sm:text-[16px] text-[#111827]/85 font-normal mt-2 leading-snug text-center">
                  Tu cita fue confirmada con éxito.
                </p>

                {/* Botones de Acción Idénticos a Imagen 5 */}
                <div className="w-full mt-8 space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onOpenAppointmentDetails && confirmedBooking) {
                        onOpenAppointmentDetails(confirmedBooking);
                      }
                    }}
                    className="w-full h-12 px-6 rounded-[10px] bg-[#00966b] hover:bg-[#00825c] text-white font-semibold text-[15px] transition shadow-xs cursor-pointer text-center"
                  >
                    Ver detalles de la cita
                  </button>

                  <button
                    type="button"
                    onClick={handleSendWhatsAppConfirmation}
                    disabled={sendingWhatsApp}
                    className="w-full h-12 px-6 rounded-[10px] border border-[#00966b] bg-white text-[#00966b] hover:bg-emerald-50/50 font-semibold text-[15px] transition cursor-pointer text-center disabled:opacity-60"
                  >
                    {sendingWhatsApp ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#00966b]" />
                        <span>Enviando confirmación...</span>
                      </span>
                    ) : whatsAppSent ? (
                      <span className="flex items-center justify-center gap-2">
                        <Check className="w-4 h-4 text-[#00966b]" />
                        <span>¡Confirmación enviada por WhatsApp!</span>
                      </span>
                    ) : (
                      <span>Enviar confirmación por WhatsApp</span>
                    )}
                  </button>

                  {whatsAppFeedback && (
                    <p className={`text-xs text-center font-medium mt-2 animate-in fade-in ${
                      whatsAppSent ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {whatsAppFeedback}
                    </p>
                  )}
                </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};
